import { useEffect, useState } from 'react';

import { ExtensionState } from './ExtensionStateProvider';

/**
 * Hook for polling extension V3 status
 * First checks for primary extension, based on NODE_ENV, and if not found checks for the secondary extension
 * - When we are on production, check for prod extension first
 * - When we are on dev, check for local extension first
 *
 * If neither are found, we return undefined for the caller function to handle
 */
export const useExtensionV3Status = () => {
  const [extensionState, setExtensionState] = useState<ExtensionState | undefined>(undefined);

  useEffect(() => {
    const interval = setInterval(() => {
      checkExtensionV3Status((state) => setExtensionState(state));
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [checkExtensionV3Status]);

  return {
    extensionV3State: extensionState,
  };
};

export const checkExtensionV3Status = (setState: (state: ExtensionState | undefined) => void) => {
  const EXTENSION_ID = (() => {
    if (process.env.NODE_ENV === 'production') {
      return { primary: process.env.EXTENSION_ID, secondary: process.env.EXTENSION_DEV_ID };
    }
    return { primary: process.env.EXTENSION_DEV_ID, secondary: process.env.EXTENSION_ID };
  })();

  const checkEnvExtensionStatus = (id?: string) => {
    try {
      chrome.runtime.sendMessage(id, 'getVersion', (response) => {
        if (!response) {
          setState(undefined);
          return false;
        }

        setState({
          extensionVersion: response.version,
          hasInstalledChromeExtension: true,
        });
        return true;
      });
    } catch {
      setState(undefined);
    }
  };

  try {
    chrome.runtime.sendMessage(EXTENSION_ID.primary, 'getVersion', (response) => {
      if (!response) {
        checkEnvExtensionStatus(EXTENSION_ID.secondary);
        return false;
      }

      setState({
        extensionVersion: response.version,
        hasInstalledChromeExtension: true,
      });

      return true;
    });
  } catch {
    checkEnvExtensionStatus(EXTENSION_ID.secondary);
  }
};
